import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useAppSelector } from '../../../store/hooks';
import { SeriesStatusEnum } from '../../../store/series/types';
import { buildSeriesTimelineRows } from '../../CalendarEvents/util/seriesTimeline';
import { resolveCardWarning } from '../lib/warningPriority';
import { findCurrentEvent } from '../lib/findCurrentEvent';
import { indexEventsToAppointments } from '../lib/indexEventToAppointment';
import { CardWarning } from '../types';
import styles from './CustomerCard.module.scss';
import { SeriesDots } from './SeriesDots';

type CustomerCardProps = {
  seriesId: string;
  isSelected: boolean;
  onClick: (seriesId: string) => void;
};

const WARNING_CLASS: Record<CardWarning, string> = {
  outcomeMissing: styles.warning_outcomeMissing,
  followupMissing: styles.warning_followupMissing,
  ratioExceeded: styles.warning_ratioExceeded,
};

const WARNING_LABEL_KEY: Record<CardWarning, string> = {
  outcomeMissing: 'seriesbooking.customerCard.outcomeMissing',
  followupMissing: 'seriesbooking.customerCard.followupMissing',
  ratioExceeded: 'seriesbooking.customerCard.ratioExceeded',
};

export const CustomerCard = React.memo(({ seriesId, isSelected, onClick }: CustomerCardProps) => {
  const intl = useIntl();

  const handleClick = useCallback(() => {
    onClick(seriesId);
  }, [seriesId, onClick]);

  const row = useAppSelector((state) => {
    const { data } = state.series.searchSeries;
    if (data === null) {
      return null;
    }
    return data.byId[seriesId];
  }, shallowEqual);

  const cardWarning = useMemo(() => {
    if (!row) {
      return null;
    }
    return resolveCardWarning(row.warnings);
  }, [row]);

  const events = useMemo(() => {
    if (!row) {
      return [];
    }
    return indexEventsToAppointments(row.events);
  }, [row]);

  const timelineRows = useMemo(() => {
    if (!row) {
      return [];
    }
    return buildSeriesTimelineRows({
      events,
      rootEventId: row.rootEventId,
      nowMs: Date.now(),
    });
  }, [row, events]);

  if (!row) {
    return null;
  }

  const currentEvent = findCurrentEvent(events);
  const currentEventId = currentEvent === null ? null : currentEvent.id;

  const isArchived = row.status === SeriesStatusEnum.CLOSED;

  const fullName = `${row.customerName} ${row.customerLastName}`.trim();

  const cardClassNames = [styles.card];
  if (isSelected) {
    cardClassNames.push(styles.card_selected);
  }
  if (isArchived) {
    cardClassNames.push(styles.card_archived);
  }

  return (
    <button type="button" className={cardClassNames.join(' ')} onClick={handleClick}>
      <div className={styles.warningSlot}>
        {cardWarning !== null && <WarningAmberIcon className={WARNING_CLASS[cardWarning]} />}
      </div>

      <div className={styles.body}>
        <div className={styles.name}>{fullName}</div>
        {cardWarning !== null && (
          <div className={[styles.warningLabel, WARNING_CLASS[cardWarning]].join(' ')}>
            {intl.formatMessage({ id: WARNING_LABEL_KEY[cardWarning] })}
          </div>
        )}
      </div>

      <SeriesDots
        rows={timelineRows}
        currentEventId={currentEventId}
        seriesOutcome={row.outcome}
        cardWarning={cardWarning}
      />

      <div className={styles.info}>i</div>
    </button>
  );
});
